import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import {
  chatsPollInterval,
  getChat,
  listChatMessages,
  listChats,
} from '@/entities/chats';
import { useAuth } from '@/features/auth';
import { ApiError } from '@/shared/api';
import { crmName, labelOf, useI18n } from '@/shared/i18n';
import { isPortrait } from '@/shared/lib/hooks/use-mobile';
import { cn } from '@/shared/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import {
  ClientChatAside,
  ClientChatComposer,
  ClientChatList,
  ClientChatThread,
} from '@/widgets/client-chat';
import { HeaderAction, PageHeader } from '@/widgets/page-header';
import { PlaygroundReviewChat } from '@/widgets/playground-review-chat';

export function ChatsPage() {
  const { t } = useI18n();
  const { hasPermission } = useAuth();
  const { dialogId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(() => !isPortrait());
  const [sideTab, setSideTab] = useState<'remarks' | 'lead'>('remarks');
  useEffect(() => {
    setSidebarOpen(!isPortrait());
  }, [dialogId]);
  const chatsQuery = useQuery({
    queryKey: ['chats'],
    queryFn: listChats,
    refetchInterval: chatsPollInterval,
  });
  const chatQuery = useQuery({
    queryKey: ['chat', dialogId],
    queryFn: () => getChat(dialogId ?? ''),
    enabled: Boolean(dialogId),
    refetchInterval: chatsPollInterval,
  });
  const messagesQuery = useQuery({
    queryKey: ['chat-messages', dialogId],
    queryFn: () => listChatMessages(dialogId ?? ''),
    enabled: Boolean(dialogId),
    refetchInterval: chatsPollInterval,
  });

  const description = chatQuery.data
    ? [labelOf(t.channels, chatQuery.data.channel), crmName(t.crm.names, chatQuery.data.stage)]
        .filter(Boolean)
        .join(' · ')
    : t.chats.description;
  const missing = chatQuery.error instanceof ApiError && chatQuery.error.status === 404;

  return (
    <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden">
      <PageHeader
        title={t.chats.title}
        description={description}
        leading={
          dialogId ? (
            <HeaderAction label={t.chats.back} render={<Link to="/chats" />}>
              <ArrowLeft className="size-4" />
            </HeaderAction>
          ) : null
        }
        sidebarOpen={sidebarOpen}
        onToggleSidebar={
          dialogId && !missing ? () => setSidebarOpen((open) => !open) : undefined
        }
      />
      <div className="relative flex min-h-0 flex-1">
        <div
          className={
            dialogId
              ? 'hidden min-w-0 overflow-hidden desktop:flex desktop:w-64 desktop:max-w-64 desktop:shrink'
              : 'flex min-w-0 flex-1 desktop:w-64 desktop:max-w-64 desktop:flex-none'
          }
        >
          <ClientChatList chats={chatsQuery.data ?? []} activeId={dialogId} />
        </div>
        <section
          className={cn(
            'min-w-0 flex-1 flex-col desktop:min-w-48',
            dialogId ? 'flex' : 'hidden desktop:flex',
          )}
        >
          {!dialogId ? (
            <p className="m-auto text-sm text-muted-foreground">{t.chats.select}</p>
          ) : missing ? (
            <p className="m-auto text-sm text-muted-foreground">{t.chats.notFound}</p>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <ClientChatThread
                messages={messagesQuery.data ?? []}
                loading={messagesQuery.isLoading}
              />
              {chatQuery.data ? <ClientChatComposer chat={chatQuery.data} /> : null}
            </div>
          )}
        </section>
        {dialogId && !missing ? (
          <aside
            className={cn(
              'min-h-0 w-full min-w-0 flex-col border-l bg-background',
              sidebarOpen
                ? 'absolute inset-0 z-20 flex desktop:static desktop:z-auto desktop:w-96 desktop:max-w-96 desktop:shrink'
                : 'hidden',
            )}
          >
            <Tabs
              value={sideTab}
              onValueChange={(value) => {
                if (value === 'remarks' || value === 'lead') setSideTab(value);
              }}
              className="flex h-full min-h-0 flex-col gap-0"
            >
              <div className="shrink-0 border-b px-3 pt-3">
                <TabsList variant="line" className="w-full">
                  <TabsTrigger value="remarks" className="flex-1 after:bottom-0">
                    {t.nav.remarks}
                  </TabsTrigger>
                  <TabsTrigger value="lead" className="flex-1 after:bottom-0">
                    {t.common.lead}
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent
                value="remarks"
                className="flex min-h-0 flex-1 flex-col overflow-hidden data-[hidden]:hidden"
              >
                <PlaygroundReviewChat
                  className="min-h-0 flex-1"
                  dialogId={dialogId}
                  scope="chats"
                  showBoard={hasPermission('can_view_remarks')}
                />
              </TabsContent>
              <TabsContent
                value="lead"
                className="min-h-0 flex-1 overflow-hidden data-[hidden]:hidden"
              >
                {chatQuery.data ? (
                  <ClientChatAside chat={chatQuery.data} />
                ) : (
                  <p className="px-4 py-6 text-sm text-muted-foreground">{t.common.loading}</p>
                )}
              </TabsContent>
            </Tabs>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
